import { Node, nodeInputRule, mergeAttributes } from "@tiptap/react";

export const inputRegex = /(!\[(.+|:?)]\((\S+)(?:(?:\s+)["'](\S+)["'])?\))/;
export const DEFAULT_WIDTH = 300;
export const DEFAULT_HEIGHT = 300;

export const Image = Node.create({
  name: "image",

  addOptions() {
    return {
      inline: false,
      HTMLAttributes: {},
      allowBase64: false,
    };
  },

  inline() {
    return this.options.inline;
  },

  group() {
    return this.options.inline ? "inline" : "block";
  },

  draggable: true,

  addAttributes() {
    return {
      src: {
        default: null
      },
      alt: {
        default: null
      },
      title: {
        default: null
      },
      width: {
        default: DEFAULT_WIDTH
      },
      height: {
        default: DEFAULT_HEIGHT
      }
    };
  },

  parseHTML() {
    return [
      {
        tag: "img[src]"
      }
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "img",
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes)
    ];
  },

  addNodeView() {
    return ({ node, getPos, editor }) => {
      const commitAttributes = (attrs) => {
        if (typeof getPos !== 'function') return;
        const pos = getPos();
        if (typeof pos !== 'number') return;
        const { state, dispatch } = editor.view;
        const currentNode = state.doc.nodeAt(pos);
        if (!currentNode) return;
        dispatch(
          state.tr.setNodeMarkup(pos, undefined, {
            ...currentNode.attrs,
            ...attrs,
          })
        );
      };

      const container = document.createElement('div');
      container.className = 'image-resizer';
      container.style.position = 'relative';
      container.style.display = 'inline-block';
      container.style.lineHeight = '0';

      const img = document.createElement('img');
      img.src = node.attrs.src;
      img.alt = node.attrs.alt || '';
      img.title = node.attrs.title || '';
      img.style.width = node.attrs.width ? `${node.attrs.width}px` : 'auto';
      img.style.height = node.attrs.height ? `${node.attrs.height}px` : 'auto';
      img.style.maxWidth = '100%';
      img.style.height = 'auto';
      img.style.display = 'block';

      // Create resize handles
      const createHandle = (cursor, position) => {
        const handle = document.createElement('div');
        handle.className = 'resize-handle';
        handle.style.position = 'absolute';
        handle.style.width = '10px';
        handle.style.height = '10px';
        handle.style.background = '#3b82f6';
        handle.style.border = '1px solid white';
        handle.style.cursor = cursor;
        handle.style.opacity = '0';
        handle.style.transition = 'opacity 0.2s';
        handle.style.zIndex = '10';
        
        // Position the handle
        Object.assign(handle.style, position);
        
        return handle;
      };

      const handles = {
        nw: createHandle('nw-resize', { top: '-5px', left: '-5px' }),
        ne: createHandle('ne-resize', { top: '-5px', right: '-5px' }),
        sw: createHandle('sw-resize', { bottom: '-5px', left: '-5px' }),
        se: createHandle('se-resize', { bottom: '-5px', right: '-5px' }),
      };

      // Add resize functionality
      Object.keys(handles).forEach(direction => {
        const handle = handles[direction];
        handle.addEventListener('mousedown', (e) => {
          e.preventDefault();
          e.stopPropagation();

          const startX = e.clientX;
          const startY = e.clientY;
          const startWidth = img.offsetWidth;
          const startHeight = img.offsetHeight;
          const aspectRatio = startWidth / startHeight;

          const handleMouseMove = (e) => {
            e.preventDefault();
            
            let newWidth, newHeight;
            const deltaX = e.clientX - startX;
            const deltaY = e.clientY - startY;

            switch (direction) {
              case 'se':
                newWidth = Math.max(50, startWidth + deltaX);
                break;
              case 'sw':
                newWidth = Math.max(50, startWidth - deltaX);
                break;
              case 'ne':
                newWidth = Math.max(50, startWidth + deltaX);
                break;
              case 'nw':
                newWidth = Math.max(50, startWidth - deltaX);
                break;
            }

            // Maintain aspect ratio
            newHeight = newWidth / aspectRatio;

            img.style.width = `${newWidth}px`;
            img.style.height = `${newHeight}px`;
          };

          const handleMouseUp = () => {
            const finalWidth = Math.round(img.offsetWidth);
            const finalHeight = Math.round(img.offsetHeight);
            commitAttributes({ width: finalWidth, height: finalHeight });

            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
          };

          document.addEventListener('mousemove', handleMouseMove);
          document.addEventListener('mouseup', handleMouseUp);
        });
      });

      // Show/hide handles on hover
      container.addEventListener('mouseenter', () => {
        Object.values(handles).forEach(handle => {
          handle.style.opacity = '1';
        });
      });

      container.addEventListener('mouseleave', () => {
        Object.values(handles).forEach(handle => {
          handle.style.opacity = '0';
        });
      });

      // Assemble the node
      container.appendChild(img);
      Object.values(handles).forEach(handle => container.appendChild(handle));

      return {
        dom: container,
        update: (updatedNode) => {
          if (updatedNode.type.name !== 'image') return false;

          // Update image attributes
          img.src = updatedNode.attrs.src;
          img.alt = updatedNode.attrs.alt || '';
          img.title = updatedNode.attrs.title || '';

          if (updatedNode.attrs.width) {
            img.style.width = `${updatedNode.attrs.width}px`;
          }
          if (updatedNode.attrs.height) {
            img.style.height = `${updatedNode.attrs.height}px`;
          }

          return true;
        },
        ignoreMutation: (mutation) => mutation.type !== 'selection',
      };
    };
  },

  addCommands() {
    return {
      setImage: options => ({ commands }) => {
        return commands.insertContent({
          type: this.name,
          attrs: options
        });
      }
    };
  },

  addInputRules() {
    return [
      nodeInputRule({
        find: inputRegex,
        type: this.type,
        getAttributes: match => {
          const [, , alt, src, title, width, height] = match;

          return { src, alt, title, width, height };
        }
      })
    ];
  }
});

export default Image;
